import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChatCircleDots, X, PaperPlaneRight } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function makeSessionId() {
  const k = "pta_chat_session";
  let s = localStorage.getItem(k);
  if (!s) {
    s = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(k, s);
  }
  return s;
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi — I'm the assistant at Pankaj Talan & Advisory. What are you looking for help with? (GST, Virtual CFO, audit, incorporation, SOPs…)" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => { sessionId.current = makeSessionId(); }, []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let done = false;
      while (!done) {
        const { value, done: d } = await reader.read();
        done = d;
        if (value) {
          const chunk = dec.decode(value, { stream: true });
          setMsgs((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: (copy[copy.length - 1].content || "") + chunk };
            return copy;
          });
        }
      }
    } catch (e) {
      setMsgs((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, connection error. Please call 9560029986." };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[var(--pta-navy)] text-[var(--pta-gold)] flex items-center justify-center shadow-2xl hover:bg-[var(--pta-gold)] hover:text-[var(--pta-navy)] transition-colors border-2 border-[var(--pta-gold)]"
        aria-label="chat"
      >
        {open ? <X size={22} weight="bold" /> : <ChatCircleDots size={24} weight="bold" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-6 z-50 w-[min(400px,calc(100vw-2rem))] h-[min(560px,calc(100vh-8rem))] flex flex-col border border-[var(--pta-gold)] bg-[var(--pta-navy)] text-[var(--pta-cream)] shadow-2xl"
            data-testid="chatbot-panel"
          >
            <div className="px-5 py-4 border-b" style={{ borderColor: 'rgba(212,175,55,0.35)' }}>
              <div className="font-mono-fine text-[10px] tracking-[0.24em] uppercase text-[var(--pta-gold)]">PTA Assistant</div>
              <div className="font-serif-display text-lg">How can we help?</div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3 py-2 leading-relaxed ${m.role === "user" ? "bg-[var(--pta-gold)] text-[var(--pta-navy)]" : "bg-white/5 text-[var(--pta-cream)] border" }`} style={m.role === 'assistant' ? { borderColor: 'rgba(212,175,55,0.25)' } : {}}>
                    {m.content || <span className="opacity-60">typing…</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t p-3 flex gap-2" style={{ borderColor: 'rgba(212,175,55,0.35)' }}>
              <input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about GST, CFO, audit…"
                className="flex-1 bg-white/5 border px-3 py-2 text-sm text-[var(--pta-cream)] placeholder-white/40 focus:outline-none"
                style={{ borderColor: 'rgba(212,175,55,0.3)' }}
              />
              <button
                data-testid="chatbot-send"
                onClick={send}
                disabled={loading}
                className="px-3 bg-[var(--pta-gold)] text-[var(--pta-navy)] hover:bg-[var(--pta-cream)]"
              >
                <PaperPlaneRight size={16} weight="bold" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
